import { useState } from "react";
import {
  Alert, Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { serviceGroupsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/** First and last day of the current month — the period a revenue review almost always covers. */
function monthBounds(d = new Date()) {
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return {
    from: iso(new Date(d.getFullYear(), d.getMonth(), 1)),
    to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

/**
 * Service revenue by group, rolled up the tree (W23).
 *
 * <p>This is the report the hierarchy was added for. "Radiology" and "Radiology — CT" used to be
 * two unrelated rows on a flat list and nothing could add them together: a department total meant
 * exporting to a spreadsheet and summing by hand, which goes wrong the first time somebody adds a
 * sub-group and forgets the formula.</p>
 *
 * <p>Every group shows two figures. <b>Own</b> is what was billed directly against it; <b>rolled
 * up</b> is that plus everything beneath. For a leaf they agree — and the difference at a
 * department is the entire point of the column.</p>
 */
export function ServiceRevenueByGroupPage() {
  const bounds = monthBounds();
  const [from, setFrom] = useState(bounds.from);
  const [to, setTo] = useState(bounds.to);

  const query = useQuery({
    queryKey: ["service-revenue-by-group", from, to],
    queryFn: () => serviceGroupsApi.revenue(from, to),
  });

  const rows = query.data ?? [];
  // Departments only, so the headline is not every level counted several times over.
  const departments = rows.filter(r => r.level === 0);
  const total = departments.reduce((s, r) => s + r.rolledUpRevenue, 0);

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Reports"]}
        title="Service revenue by group"
        subtitle="What each department earned, including everything under it. A group's own figure is what was billed directly against it."
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} helperText="Inclusive" />
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <StatCard label="Service revenue" value={inr.format(total)}
          hint="posted bills, net of discount" />
        <StatCard label="Departments" value={departments.length} />
        <StatCard label="Groups in total" value={rows.length}
          hint={`${rows.length - departments.length} sub-group(s)`} />
      </Stack>

      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
      {query.isLoading && (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
      )}

      <Paper sx={{ p: 2 }}>
        {!query.isLoading && rows.length === 0 && (
          <Alert severity="info">
            No service groups yet. Revenue is reported by group, so services with no group do not
            appear here at all.
          </Alert>
        )}

        {rows.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Lines</TableCell>
                  <TableCell align="right" sx={{ width: 150 }}>Own</TableCell>
                  <TableCell align="right" sx={{ width: 150 }}>Rolled up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  // A group whose subtree earned more than it did itself has children carrying
                  // revenue — the case the flat list could never show.
                  const hasChildren = r.rolledUpRevenue !== r.ownRevenue
                    || r.rolledUpLineCount !== r.ownLineCount;
                  return (
                    <TableRow key={r.groupId} hover>
                      <TableCell>
                        {/* Indented by depth, so a flat table reads as the tree it is. */}
                        <Box sx={{ pl: r.level * 3 }}>
                          <Stack spacing={0}>
                            <span>{r.name}</span>
                            <Typography variant="caption" color="text.secondary">
                              {r.code}{r.level === 0 ? " · department" : ""}
                            </Typography>
                          </Stack>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{r.rolledUpLineCount}</TableCell>
                      <TableCell align="right">
                        {r.ownRevenue === 0
                          ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
                          : inr.format(r.ownRevenue)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={r.level === 0 && hasChildren ? 600 : 400}>
                          {inr.format(r.rolledUpRevenue)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
